var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import './SystemOverview.scss';
import { ApiRequests } from '../ApiRequests';
import { createAsyncSelectors } from '@lib/selectors';
import empty from 'images/empty-overview-apps.svg';
import { GraphBehavior } from './GraphBehaviors';
import { GraphLegend } from './GraphLegend';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import Salesforce from 'images/salesforce-logo.png';
import { SonarText } from '@ui/text';
import uniqBy from 'lodash/uniqBy';
import { ViewConnectedAppsButton } from './ViewConnectedAppsButton';
import { buildEdge, buildImageMetadata, OverviewGraph } from './OverviewGraph';
import { CLASSIFICATION_COLORS, COPY_SPECIFIC, DATA_SECURITY_DASHBOARD, TAG_COLORS } from '@sonar-software/sonar-ts-constants';
import { Col, InputNumber, Row, Space } from 'antd';
import { coreActions, DataSecurityApplicationStatus, defaultComboForceOptions, emptyGraphData, FeatureFlag } from '@sonar-software/sonar-core';
import { Else, If, Then, When } from 'react-if';
import { GraphConfigEditor, GraphLayouts, GraphLayoutSelector, GraphLines, GraphLineSelector } from '@security/graph-tools';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { SanitizedHtml, SonarCard } from '@ui/general';
import { useAsyncStateWithPreload, useDataSecurityApps, useFeatureFlag, useSonarSelector } from '@hooks';
function buildImageMetadataLookup(apps) {
    return __awaiter(this, void 0, void 0, function* () {
        const results = apps.map((app) => buildImageMetadata(app.applicationId, app.name, app.label));
        const allResults = yield Promise.all(results);
        return allResults.reduce((prev, next) => {
            prev[next.id] = next;
            return prev;
        }, {});
    });
}
function buildGroupCombos(entries, hideDetected) {
    const combos = uniqBy(entries, (entry) => entry.stakeholderGroupId)
        .filter((entry) => Boolean(entry.stakeholderGroupId))
        .map((entry, index) => {
        var _a;
        return ({
            id: `${(_a = entry.stakeholderGroupId) !== null && _a !== void 0 ? _a : 0}-combo`,
            style: {
                fill: `${TAG_COLORS[index % TAG_COLORS.length]}19`,
                stroke: TAG_COLORS[index % TAG_COLORS.length]
            },
            type: 'rect'
        });
    });
    if (!hideDetected && entries.some((entry) => entry.status === DataSecurityApplicationStatus.Detected)) {
        combos.push({
            id: 'detected-combo',
            type: 'rect'
        });
    }
    return combos;
}
function buildClassificationCombos(entries) {
    return uniqBy(entries, (entry) => entry.classification)
        .filter((entry) => Boolean(entry.classification))
        .map((entry) => { var _a; return (_a = entry.classification) !== null && _a !== void 0 ? _a : ''; })
        .map((entry) => ({
        id: `${entry}-combo`,
        style: {
            fill: `${CLASSIFICATION_COLORS[entry]}19`,
            stroke: CLASSIFICATION_COLORS[entry]
        },
        type: 'rect'
    }));
}
function buildEdges(entries, hideDetected, lineType) {
    return entries
        .filter((entry) => (hideDetected ? entry.status !== DataSecurityApplicationStatus.Detected : true))
        .map((entry) => buildEdge(entry.read, entry.write, lineType, String(entry.applicationId), 'salesforce'));
}
function getComboForNode(entry, groupByType) {
    if (entry.status === DataSecurityApplicationStatus.Detected) {
        return 'detected-combo';
    }
    const comboId = groupByType === 'group' ? entry.stakeholderGroupId : entry.classification;
    return comboId ? `${comboId}-combo` : undefined;
}
function buildNodes(entries, appLookup, hideDetected, organizeByType) {
    return [
        {
            id: 'salesforce',
            img: Salesforce,
            size: [178, 121],
            type: 'image'
        },
        ...entries.filter((entry) => (hideDetected ? entry.status !== DataSecurityApplicationStatus.Detected : true))
            .map((entry) => {
            var _a;
            return ({
                comboId: getComboForNode(entry, organizeByType),
                detected: entry.status === DataSecurityApplicationStatus.Detected,
                id: String(entry.applicationId),
                metadata: (_a = appLookup[entry.applicationId]) !== null && _a !== void 0 ? _a : {
                    name: `Undefined App ${entry.applicationId}`
                },
                type: 'app-display'
            });
        })
    ];
}
const appOverviewSelectors = createAsyncSelectors((state) => state.core.dataSecurity.applicationOverview.get);
const getDataSecurityApiRequestUsageSelector = createAsyncSelectors((state) => state.core.dataSecurity.apiRequestUsage.get);
export const SystemOverview = memo(({ expanded = false }) => {
    var _a, _b;
    const displayTools = useFeatureFlag(FeatureFlag.DataSecurityGraphTools);
    const [hideDetected, setHideDetected] = useState(false);
    const [organizeBy, setOrganizeBy] = useState('group');
    const [layoutType, setLayoutType] = useState(GraphLayouts[3].value);
    const [lineType, setLineType] = useState(GraphLines[12].value);
    const [mockAppCount, setMockAppCount] = useState(0);
    const [appMetadata, setAppMetadata] = useState(null);
    const [maxZoom, setMaxZoom] = useState(2);
    const [minZoom, setMinZoom] = useState(0.25);
    const [layoutOptions, setLayoutOptions] = useState(Object.assign(Object.assign({}, defaultComboForceOptions), { type: layoutType }));
    const applications = useDataSecurityApps(!expanded, true);
    const appsLoaded = useSonarSelector(useDataSecurityApps.selectors.isReceived);
    const { data: apiRequestData } = useAsyncStateWithPreload(getDataSecurityApiRequestUsageSelector, coreActions.requestGetCoreDataSecurityApiRequestUsage, !expanded);
    const { data } = useAsyncStateWithPreload(appOverviewSelectors, coreActions.requestGetCoreDataSecurityApplicationOverview, !expanded);
    useEffect(() => {
        if (!appMetadata && appsLoaded) {
            buildImageMetadataLookup(applications).then((result) => {
                setAppMetadata(result);
            });
        }
    }, [appMetadata, applications, appsLoaded]);
    const graphData = useMemo(() => {
        const mergedData = data ? [
            ...data.map((entry) => {
                var _a, _b;
                return (Object.assign(Object.assign({}, entry), { classification: (_b = (_a = applications.find((app) => app.applicationId === entry.applicationId)) === null || _a === void 0 ? void 0 : _a.classification) === null || _b === void 0 ? void 0 : _b.toLowerCase() }));
            }),
            ...Array.from(Array(mockAppCount).keys()).map((val) => ({
                applicationId: val + 500,
                read: 1,
                status: DataSecurityApplicationStatus.Detected,
                write: 0
            }))
        ] : [];
        return {
            combos: organizeBy === 'group' ?
                buildGroupCombos(mergedData, hideDetected) : buildClassificationCombos(mergedData),
            edges: buildEdges(mergedData, hideDetected, lineType),
            nodes: buildNodes(mergedData, appMetadata !== null && appMetadata !== void 0 ? appMetadata : {}, hideDetected, organizeBy)
        };
    }, [appMetadata, applications, data, organizeBy, hideDetected, lineType, mockAppCount]);
    const isReady = data && appMetadata;
    const showGraph = (data === null || data === void 0 ? void 0 : data.length) || mockAppCount;
    return (React.createElement(SonarCard, { id: 'system-overview-card' },
        React.createElement(Row, null,
            React.createElement(Col, { span: 12 },
                React.createElement(Space, { className: 'system-overview-buttons', direction: 'vertical', size: 'small' },
                    React.createElement("div", { className: 'h2' }, DATA_SECURITY_DASHBOARD.SYSTEM_OVERVIEW),
                    React.createElement(ViewConnectedAppsButton, null))),
            (apiRequestData === null || apiRequestData === void 0 ? void 0 : apiRequestData.dailyApiRequests) &&
                React.createElement(Col, { span: 12 },
                    React.createElement(ApiRequests, { header: DATA_SECURITY_DASHBOARD.API_REQUESTS_LAST_24, maxCalls: apiRequestData.dailyApiRequests.max, tooltip: React.createElement(SanitizedHtml, { as: 'span', html: DATA_SECURITY_DASHBOARD.API_REQUESTS_TOOLTIP }), usedCalls: ((_a = apiRequestData === null || apiRequestData === void 0 ? void 0 : apiRequestData.dailyApiRequests) === null || _a === void 0 ? void 0 : _a.max) -
                            ((_b = apiRequestData === null || apiRequestData === void 0 ? void 0 : apiRequestData.dailyApiRequests) === null || _b === void 0 ? void 0 : _b.remaining) }))),
        React.createElement(LoadingSpinner, { minDuration: 1500, opaque: true, spinning: !isReady },
            React.createElement(If, { condition: showGraph },
                React.createElement(Then, null,
                    React.createElement(OverviewGraph, { data: isReady ? graphData : emptyGraphData, layoutOptions: layoutOptions, maxZoom: maxZoom, minZoom: Number(minZoom) },
                        React.createElement(GraphBehavior, null),
                        React.createElement(GraphLegend, { expanded: expanded, extra: React.createElement(When, { condition: displayTools },
                                React.createElement(Space, null,
                                    React.createElement(GraphLayoutSelector, { onChange: (value) => {
                                            setLayoutOptions(Object.assign(Object.assign({}, layoutOptions), { type: value }));
                                            setLayoutType(value);
                                        }, value: layoutType }),
                                    React.createElement(GraphLineSelector, { onChange: setLineType, value: lineType }),
                                    React.createElement(InputNumber, { min: 0, onChange: (value) => setMockAppCount(value !== null && value !== void 0 ? value : 0), value: mockAppCount }),
                                    React.createElement(InputNumber, { min: 0, onChange: (value) => setMaxZoom(value !== null && value !== void 0 ? value : 2), value: maxZoom }),
                                    React.createElement(InputNumber, { min: 0, onChange: (value) => setMinZoom(value !== null && value !== void 0 ? value : 0.25), step: 0.1, stringMode: true, value: minZoom }))), groupIds: graphData.combos.map((entry) => entry.id), hideDetected: hideDetected, onHideDetectedChange: setHideDetected, onOrganizeByChange: setOrganizeBy, organizeBy: organizeBy })),
                    React.createElement(When, { condition: displayTools },
                        React.createElement(GraphConfigEditor, { onChange: setLayoutOptions, value: layoutOptions }))),
                React.createElement(Else, null,
                    React.createElement("div", { className: 'empty-graph' },
                        React.createElement(Space, { direction: 'vertical', size: 'large' },
                            React.createElement(SonarText, { size: 16, weight: 'semibold' }, COPY_SPECIFIC.NO_CONNECTED_APPS),
                            React.createElement("img", { alt: COPY_SPECIFIC.NO_CONNECTED_APPS, className: 'empty-apps', src: empty }))))))));
});
